import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CommonApiService } from './common-api.service';
import {
  CategoryOperations,
  EssentialFormOperations,
  FormOperations,
  ParticipantOperations,
  QuestionOperations,
  RuleOperations,
  SectionId,
  SubSectionId,
  TemplateOperations,
  VariableOperations,
} from '../../shared/constants/role-permission-constants';

const NOTIFY_ENDPOINT = 'EmailToAdmin/NotifySuperAdmin';
const DEDUPE_PREFIX = 'fw-admin-notify:';
const SQL_ERROR_RE =
  /sql(?:state|exception|client)?|ole db|odbc|deadlock|timeout expired|violation of (?:unique|primary)|cannot insert|invalid (?:object|column) name|string or binary data would be truncated|unclosed quotation mark/i;

const SKIP_URL_RE =
  /\/(login|signup|forgot-password|404|landing|hubspot|forms\/basic|forms\/client)(\/|$|\?)/i;

const DETAIL_GET_RE = /\/(FWIIForm|Forms|FormFields|Question|Template|Templates|Packets)\/(GetById|GetQuestionInfo)/i;

@Injectable({
  providedIn: 'root',
})
export class AdminNotifyService {
  constructor(private api: CommonApiService) {}

  inspectCurrentPage(url: string): void {
    try {
      this.inspectCurrentPageSafe(url);
    } catch {
      // never block navigation
    }
  }

  private inspectCurrentPageSafe(url: string): void {
    if (environment.isSuperAdmin || this.shouldSkipPage(url)) {
      return;
    }
    if (!localStorage.getItem('sp-token')) {
      return;
    }

    const permissions = this.readPermissions();
    if (!permissions) {
      this.notifyMissingPermissions('Stored permission_data is missing or invalid');
      return;
    }
    if (!permissions.length) {
      this.notifyMissingPermissions('Permission list was empty');
      return;
    }

    if (this.isFullyPermitted(permissions)) {
      return;
    }

    if (this.isSubscriptionMissing()) {
      this.notifyMissingSettings(url);
      return;
    }

    const path = (url || '').split('?')[0];
    if (path === '/advance' || path === '/advance/') {
      if (!this.hasAnyModuleGet(permissions)) {
        this.notifyNoModulePermissions(url);
      }
    }
  }

  notifyMissingPermissions(detail: string): void {
    this.send(
      'missing-permissions',
      'FormWiz: User permissions were not loaded',
      this.buildBody(
        'User permissions were not loaded',
        'Login succeeded, but FormWiz permissions were not returned. The user would see a blank page.',
        detail,
      ),
    );
  }

  notifyNoModulePermissions(pageUrl: string): void {
    this.send(
      'no-module-permissions',
      'FormWiz: No module permissions assigned',
      this.buildBody(
        'No module permissions assigned',
        'The user is logged in, but no FormWiz module (Documents, Templates, Questions, etc.) has Get permission. The dashboard is blank.',
        'No Get permission on any FormWiz module',
      ),
    );
  }

  notifyMissingSettings(pageUrl: string): void {
    this.send(
      'missing-settings',
      'FormWiz: Subscription or account settings missing',
      this.buildBody(
        'Subscription or account settings missing',
        'The user is logged in, but FormWiz/Document subscription flags are missing. Feature cards stay hidden and the page looks blank.',
        'FormWiz and Document subscription flags are both off',
      ),
    );
  }

  handleApiSuccess(requestUrl: string, body: any, pageUrl: string): void {
    try {
      this.handleApiSuccessSafe(requestUrl, body, pageUrl);
    } catch {
      // never block the original API
    }
  }

  private handleApiSuccessSafe(requestUrl: string, body: any, pageUrl: string): void {
    if (environment.isSuperAdmin || this.shouldSkipPage(pageUrl)) {
      return;
    }
    if (!requestUrl || requestUrl.includes(NOTIFY_ENDPOINT)) {
      return;
    }
    if (this.isSqlError(body) || this.isSqlError(requestUrl)) {
      return;
    }
    if (!body || body.error === true) {
      return;
    }
    if (!DETAIL_GET_RE.test(requestUrl)) {
      return;
    }
    if (!this.isEditPage(pageUrl)) {
      return;
    }
    if (!this.isEmptySuccessPayload(body)) {
      return;
    }

    const endpoint = this.endpointName(requestUrl);
    this.send(
      `blank-after-success:${endpoint}`,
      `FormWiz: Blank page after successful load (${endpoint})`,
      this.buildBody(
        'Blank page after successful load',
        'The API returned success, but no document/question/template payload was present. The edit page is blank.',
        `Empty payload from ${endpoint}`,
        [{ label: 'API', value: endpoint }],
      ),
    );
  }

  isSqlError(value: unknown): boolean {
    const text = this.toErrorText(value);
    return !!text && SQL_ERROR_RE.test(text);
  }

  private send(dedupeKey: string, subject: string, body: string): void {
    if (environment.isSuperAdmin) {
      return;
    }
    if (this.isSqlError(subject) || this.isSqlError(body)) {
      return;
    }
    if (this.alreadySent(dedupeKey)) {
      return;
    }
    this.markSent(dedupeKey);
    this.api.allPostMethod(NOTIFY_ENDPOINT, { subject, body }).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  private shouldSkipPage(url: string): boolean {
    const path = url || '';
    return SKIP_URL_RE.test(path) || path.includes('/hubspot');
  }

  private isEditPage(url: string): boolean {
    return /\/(edit-document|edit-question|edit-template|edit-rule|edit-participant|edit-dynamic|edit-messaging)/i.test(
      url || '',
    );
  }

  private isEmptySuccessPayload(body: any): boolean {
    const data = body?.data;
    const imageData = body?.imageData;
    const hasImage = Array.isArray(imageData) ? imageData.length > 0 : !!imageData;
    const hasName = !!(body?.name || body?.formDocumentFilePath);
    const hasData = this.hasValue(data);
    return !hasData && !hasImage && !hasName;
  }

  private hasValue(value: unknown): boolean {
    if (value == null) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value as object).length > 0;
    }
    return true;
  }

  private readPermissions(): any[] | null {
    const raw = localStorage.getItem('permission_data');
    if (!raw || raw === 'null' || raw === 'undefined') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private isFullyPermitted(permissions: any[]): boolean {
    return permissions.some(
      (permission: any) =>
        permission.sectionId === SectionId.FormWiz &&
        permission.subSectionId === 0 &&
        permission.subSectionOperationId === 0,
    );
  }

  private hasAnyModuleGet(permissions: any[]): boolean {
    const checks: Array<[number, number]> = [
      [SubSectionId.Form, FormOperations.Get],
      [SubSectionId.EssentialForm, EssentialFormOperations.Get],
      [SubSectionId.Template, TemplateOperations.Get],
      [SubSectionId.Question, QuestionOperations.Get],
      [SubSectionId.Rule, RuleOperations.Get],
      [SubSectionId.Participant, ParticipantOperations.Get],
      [SubSectionId.Category, CategoryOperations.Get],
      [SubSectionId.Variable, VariableOperations.Get],
    ];
    return checks.some(([subSectionId, operationId]) =>
      permissions.some(
        (item: any) =>
          item.subSectionId == subSectionId && item.subSectionOperationId == operationId,
      ),
    );
  }

  private isSubscriptionMissing(): boolean {
    const raw = localStorage.getItem('sp-user');
    if (!raw) {
      return true;
    }
    try {
      const user = JSON.parse(raw);
      return user?.hasFormWizSubscription !== true && user?.hasNormalSubscription !== true;
    } catch {
      return true;
    }
  }

  private userContext(): { label: string; value: string }[] {
    let user: any = {};
    try {
      user = JSON.parse(localStorage.getItem('sp-user') || '{}') || {};
    } catch {
      user = {};
    }
    return [
      { label: 'User', value: String(user.fullName || localStorage.getItem('userName') || '-') },
      { label: 'User ID', value: String(user.userId || localStorage.getItem('userId') || '-') },
      { label: 'Email', value: String(user.email || user.userEmail || user.userName || '-') },
      { label: 'Account', value: String(user.accountName || user.companyName || '-') },
      { label: 'Page', value: typeof location !== 'undefined' ? location.href : '-' },
      { label: 'Time', value: new Date().toUTCString() },
    ];
  }

  private buildBody(
    title: string,
    summary: string,
    detail: string,
    extraRows: { label: string; value: string }[] = [],
  ): string {
    const rows = [...extraRows, ...this.userContext()]
      .map(
        (row) =>
          `<tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;background:#f8fafc;font-weight:600;width:140px;color:#334155;">${this.escapeHtml(row.label)}</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;color:#0f172a;">${this.escapeHtml(row.value)}</td>
          </tr>`,
      )
      .join('');

    return `
      <div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#0f172a;">
        <h2 style="margin:0 0 12px;font-size:18px;color:#1e3a8a;">${this.escapeHtml(title)}</h2>
        <p style="margin:0 0 12px;">${this.escapeHtml(summary)}</p>
        <p style="margin:0 0 16px;"><strong>Reason:</strong> ${this.escapeHtml(detail)}</p>
        <table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;max-width:560px;">
          ${rows}
        </table>
      </div>
    `.trim();
  }

  private escapeHtml(value: string): string {
    return String(value || '-')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private endpointName(requestUrl: string): string {
    const match = requestUrl.match(/api\/([^?]+)/i);
    return match?.[1] || requestUrl;
  }

  private toErrorText(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      return [obj['message'], obj['error'], obj['title'], JSON.stringify(value)].filter(Boolean).join(' ');
    }
    return String(value);
  }

  private alreadySent(key: string): boolean {
    try {
      return sessionStorage.getItem(DEDUPE_PREFIX + key) === '1';
    } catch {
      return false;
    }
  }

  private markSent(key: string): void {
    try {
      sessionStorage.setItem(DEDUPE_PREFIX + key, '1');
    } catch {
      // ignore quota / private mode
    }
  }
}
